import React, { useState, useEffect, useRef } from "react";
import Header from "../../../Componets/Header";
import toast from "react-hot-toast";
import moment from "moment";
import { get_all_invoices } from "../../../Controllers/Sales/Invoices";
import { get_all_sales_returns } from "../../../Controllers/Sales/Returns";
import ReactToPrint from "react-to-print";
import { Button } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import Loadercomponent from "../../../Componets/Loader";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerCairoFont } from "../cairo-font";
import ScrollWrapper from "../../../Componets/ScrollWrapper";
import formatDateDDMMYYYY from "../../../Main/formatDate";

const SalesReportReplica = () => {
  const [loader, setLoader] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [salesReturns, setSalesReturns] = useState([]);
  const [reportData, setReportData] = useState(null);
  let componentRef = useRef();

  const headers = {
    name: "Daily Sales Report",
    components: [
      {
        type: "range",
        name: "date",
        value: {
          start: moment().format("YYYY-MM-DD"),
          end: moment().format("YYYY-MM-DD"),
        },
      },
    ],
    navigate: {
      tabs: [
        {
          name: "Reports",
          path: "/app/reports",
        },
        {
          name: "Daily Sales",
          path: ""
        }
      ],
      total: [
        { value: 0, label: "Total" },
        { value: 0, label: "Active" },
        { value: "0", label: "Inactive" },
      ],
    },
  };

  const [header, setHeader] = useState(headers);

  const fetchData = async (tabledata) => {
    try {
      setLoader(true);

      const data = {
        search: tabledata?.search,
        date: tabledata?.date,
        source: "Daily Sales Report",
      };

      const invoicesData = await get_all_invoices(data);
      const returnData = await get_all_sales_returns(data);

      if (invoicesData?.status && returnData?.status) {
        setInvoices(invoicesData?.data?.data || []);
        setSalesReturns(returnData?.data?.data || []);
        processReportData(
          invoicesData?.data?.data || [],
          returnData?.data?.data || []
        );
        setLoader(false);
      } else {
        toast?.error(invoicesData?.message || returnData?.message || "Failed to fetch data");
        setLoader(false);
      }
    } catch (errors) {
      toast?.error(errors?.message);
    }
  };

  const processReportData = (invoices, salesReturns) => {
    // Calculate sales totals
    const totalSales = invoices.reduce(
      (sum, invoice) => sum + invoice.total,
      0
    );
    const totalSalesTransactions = invoices.length;

    // Calculate sales returns totals
    const totalReturns = salesReturns.reduce(
      (sum, returnItem) => sum + returnItem.total,
      0
    );
    const totalReturnsTransactions = salesReturns.length;

    // Calculate net sales
    const netSales = totalSales - totalReturns;
    const totalTransactions = totalSalesTransactions + totalReturnsTransactions;

    // Calculate tax amounts
    const salesTaxAmount = invoices.reduce(
      (sum, invoice) => sum + (invoice.taxamount || 0),
      0
    );
    const returnsTaxAmount = salesReturns.reduce(
      (sum, returnItem) => sum + (returnItem.taxamount || 0),
      0
    );
    const netTaxAmount = salesTaxAmount - returnsTaxAmount;

    // Process payment methods
    const paymentMethods = {};
    
    // Sales payments
    invoices.forEach((invoice) => {
      if (invoice.payments && invoice.payments.length > 0) {
        invoice.payments.forEach((payment) => {
          if (paymentMethods[payment.name]) {
            paymentMethods[payment.name] += payment.amount;
          } else {
            paymentMethods[payment.name] = payment.amount;
          }
        });
      }
    });

    // Adjust for returns (returns should reduce payments)
    // Note: This assumes returns are refunded through same payment methods
    salesReturns.forEach((returnItem) => {
      // If returns have payment records, subtract them
      // Otherwise, you might want to add a "Refunds" category
      // For now, we'll create a separate refunds category
    });

    const payments = Object.keys(paymentMethods).map((method) => ({
      method,
      amount: paymentMethods[method],
    }));

    // Add refunds category if there are returns
    if (totalReturns > 0) {
      payments.push({
        method: "Refunds",
        amount: -totalReturns, // Negative to show as deduction
      });
    }

    // Calculate discounts
    const salesDiscountTotal = invoices.reduce(
      (sum, invoice) => sum + invoice.discount,
      0
    );
    const returnsDiscountTotal = salesReturns.reduce(
      (sum, returnItem) => sum + (returnItem.discount || 0),
      0
    );
    const netDiscountTotal = salesDiscountTotal - returnsDiscountTotal;

    const salesDiscountCount = invoices.filter(
      (invoice) => invoice.discount > 0
    ).length;
    const returnsDiscountCount = salesReturns.filter(
      (returnItem) => returnItem.discount > 0
    ).length;
    const totalDiscountCount = salesDiscountCount + returnsDiscountCount;

    // Get date range from header
    const dateRange = header.components.find(c => c.name === "date")?.value;
    const startDate = dateRange?.start ? formatDateDDMMYYYY(dateRange.start) : moment().format("MM/DD/YYYY");
    const endDate = dateRange?.end ? formatDateDDMMYYYY(dateRange.end) : moment().format("MM/DD/YYYY");

    const dateDisplay = startDate === endDate ? startDate : `${startDate} - ${endDate}`;

    const processedData = {
      storeName: "Your Store Name",
      country: "Oman",
      title: "Daily Sales Report",
      sessionId: `Session/${moment().format("YYYYMMDD")}`,
      dateRange: dateDisplay,
      sales: [
        {
          name: "Sales",
          details: "Total sales transactions",
          qty: totalSalesTransactions,
          amount: totalSales,
        },
        {
          name: "Returns",
          details: "Sales returns/refunds",
          qty: totalReturnsTransactions,
          amount: -totalReturns, // Negative to show as deduction
        },
        {
          name: "Net Sales",
          details: "Sales - Returns",
          qty: totalTransactions,
          amount: netSales,
        }
      ],
      taxes: {
        label: "NET TAX",
        amount: netTaxAmount,
        breakdown: {
          salesTax: salesTaxAmount,
          returnsTax: returnsTaxAmount,
        }
      },
      payments: payments,
      discounts: {
        count: totalDiscountCount,
        amount: netDiscountTotal,
        breakdown: {
          salesDiscounts: salesDiscountTotal,
          returnsDiscounts: returnsDiscountTotal,
        }
      },
      sessionControl: {
        total: netSales,
        transactions: totalTransactions,
        salesTransactions: totalSalesTransactions,
        returnsTransactions: totalReturnsTransactions,
        name: "Session Total",
        expected: netSales + netTaxAmount,
      },
      note: "Automatically generated sales report including returns",
      summary: {
        totalSales: totalSales,
        totalReturns: totalReturns,
        netSales: netSales,
      }
    };

    setReportData(processedData);
  };

  const downloadPDF = () => {
    if (!reportData) {
      toast.error("No report data available to download");
      return;
    }

    try {
      const {
        storeName,
        country,
        title,
        sessionId,
        dateRange,
        sales,
        taxes,
        payments,
        discounts,
        sessionControl,
        note,
        summary,
      } = reportData;

      const doc = new jsPDF();
      registerCairoFont(doc);
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFont("Cairo-Bold", "bold");

      doc.setFontSize(16);
      doc.text(title, pageWidth / 2, 15, { align: "center" });

      doc.setFontSize(10);
      doc.text(`Session ID: ${sessionId}`, pageWidth / 2, 22, { align: "center" });
      doc.text(`Date: ${dateRange}`, pageWidth / 2, 28, { align: "center" });

      let startY = 35;

      // Summary section (quick overview)
      doc.setFontSize(11);
      doc.text("Summary", 14, startY);
      
      const summaryData = [
        ["Total Sales", formatCurrencyPDF(summary.totalSales)],
        ["Total Returns", formatCurrencyPDF(summary.totalReturns)],
        ["Net Sales", formatCurrencyPDF(summary.netSales)]
      ];
      
      autoTable(doc, {
        startY: startY + 5,
        body: summaryData,
        theme: 'grid',
        styles: {
          font: "Cairo",
          textColor: [0, 0, 0],
          lineColor: [7, 181, 83],
          lineWidth: 0.2,
          fontSize: 9
        },
        headStyles: {
          font: "Cairo",
          fontStyle: "bold",
          fillColor: false,
          lineColor: [7, 181, 83],
          lineWidth: 0.6
        },
        columnStyles: {
          1: { halign: 'right', fontStyle: 'bold' }
        }
      });

      const summaryEnd = doc.lastAutoTable.finalY + 10;

      // Detailed Sales section
      doc.setFontSize(12);
      doc.text("Sales Details", 14, summaryEnd);

      const salesData = sales.map(s => [
        s.name,
        s.qty.toString(),
        formatCurrencyPDF(s.amount)
      ]);

      autoTable(doc, {
        startY: summaryEnd + 5,
        head: [["Name", "Quantity", "Amount"]],
        body: salesData,
        theme: 'grid',
        styles: {
          font: "Cairo",
          textColor: [0, 0, 0],
          lineColor: [7, 181, 83],
          lineWidth: 0.2,
          fontSize: 9
        },
        headStyles: {
          font: "Cairo",
          fontStyle: "bold",
          fillColor: false,
          lineColor: [7, 181, 83],
          lineWidth: 0.6
        },
        columnStyles: {
          2: { halign: 'right' }
        }
      });

      const salesEnd = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text("Taxes on Sales", 14, salesEnd);

      const taxData = [
        ["Sales Tax", formatCurrencyPDF(taxes.breakdown?.salesTax || 0)],
        ["Returns Tax", formatCurrencyPDF(taxes.breakdown?.returnsTax || 0)],
        ["Net Tax", formatCurrencyPDF(taxes.amount)]
      ];

      autoTable(doc, {
        startY: salesEnd + 5,
        body: taxData,
        theme: 'grid',
        styles: {
          font: "Cairo",
          textColor: [0, 0, 0],
          lineColor: [7, 181, 83],
          lineWidth: 0.2,
          fontSize: 9
        },
        headStyles: {
          font: "Cairo",
          fontStyle: "bold",
          fillColor: false,
          lineColor: [7, 181, 83],
          lineWidth: 0.6
        },
        columnStyles: {
          1: { halign: 'right' },
          2: { fontStyle: 'bold' }
        }
      });

      const taxesEnd = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text("Payments", 14, taxesEnd);

      const paymentData = payments.map(p => [
        p.method,
        formatCurrencyPDF(p.amount)
      ]);

      autoTable(doc, {
        startY: taxesEnd + 5,
        head: [["Payment Method", "Amount"]],
        body: paymentData,
        theme: 'grid',
        styles: {
          font: "Cairo",
          textColor: [0, 0, 0],
          lineColor: [7, 181, 83],
          lineWidth: 0.2,
          fontSize: 9
        },
        headStyles: {
          font: "Cairo",
          fontStyle: "bold",
          fillColor: false,
          lineColor: [7, 181, 83],
          lineWidth: 0.6
        },
      });

      const paymentsEnd = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text("Discounts", 14, paymentsEnd);

      const discountData = [
        ["Sales Discounts", formatCurrencyPDF(discounts.breakdown?.salesDiscounts || 0)],
        ["Returns Discounts", formatCurrencyPDF(discounts.breakdown?.returnsDiscounts || 0)],
        ["Number of discounts", discounts.count.toString()],
        ["Net Discounts", formatCurrencyPDF(discounts.amount)]
      ];

      autoTable(doc, {
        startY: paymentsEnd + 5,
        body: discountData,
        theme: 'grid',
        styles: {
          font: "Cairo",
          textColor: [0, 0, 0],
          lineColor: [7, 181, 83],
          lineWidth: 0.2,
          fontSize: 9
        },
        headStyles: {
          font: "Cairo",
          fontStyle: "bold",
          fillColor: false,
          lineColor: [7, 181, 83],
          lineWidth: 0.6
        },
      });

      const discountsEnd = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text("Session Control", 14, discountsEnd);

      const sessionData = [
        ["Total Sales Transactions", sessionControl.salesTransactions.toString()],
        ["Total Returns Transactions", sessionControl.returnsTransactions.toString()],
        ["Total Transactions", sessionControl.transactions.toString()],
        ["Net Sales Total", formatCurrencyPDF(sessionControl.total)],
        ["Name", sessionControl.name],
        ["Expected", formatCurrencyPDF(sessionControl.expected)]
      ];

      autoTable(doc, {
        startY: discountsEnd + 5,
        body: sessionData,
        theme: 'grid',
        styles: {
          font: "Cairo",
          textColor: [0, 0, 0],
          lineColor: [7, 181, 83],
          lineWidth: 0.2,
          fontSize: 9
        },
        headStyles: {
          font: "Cairo",
          fontStyle: "bold",
          fillColor: false,
          lineColor: [7, 181, 83],
          lineWidth: 0.6
        },
        columnStyles: {
          1: { halign: 'right' }
        }
      });

      const sessionEnd = doc.lastAutoTable.finalY + 10;
      if (note) {
        doc.setFontSize(9);
        doc.text(`Note: ${note}`, 14, sessionEnd);
      }

      const formattedDate = moment().format('DD_MM_YYYY');
      doc.save(`Daily_Sales_Report_${formattedDate}.pdf`);
      toast.success("PDF downloaded successfully");

    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF file");
    }
  };

  const formdata = (data) => {
    fetchData(data);
  };

  useEffect(() => {
    fetchData({
      date: {
        start: moment().format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD"),
      },
    });
  }, []);

  if (loader) {
    return (
      <div className="max-w-3xl mx-auto p-6 bg-white text-center">
        Loading report data...
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="max-w-3xl mx-auto p-6 bg-white text-center">
        No report data available
      </div>
    );
  }

  const {
    storeName,
    country,
    title,
    sessionId,
    dateRange,
    sales,
    taxes,
    payments,
    discounts,
    sessionControl,
    note,
    summary,
  } = reportData;

  return (
    <Header headers={header} setHeaders={setHeader} formdata={formdata}>
      <Loadercomponent loader={loader}>
        <div>
          <div className="text-right flex gap-3 justify-end mt-4">
            <Button
              onClick={downloadPDF}
              className="mr-2  !bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82]"
              type="primary"
            >
              Download PDF
            </Button>
            
            <ReactToPrint
              trigger={() => (
                <Button
                  className="!bg-grey-500 !border-grey-500 hover:!bg-grey-600 hover:!border-grey-600"
                  type="primary"
                >
                  <PrinterOutlined /> Print Report
                </Button>
              )}
              content={() => componentRef}
              pageStyle={`
                @page {
                  size: landscape;
                  margin: 10mm;
                }
                
                @media print {
                  tfoot {
                    position: static !important;
                    bottom: auto !important;
                  }
                  thead { 
                    display: table-header-group !important;
                  }
                  tfoot {
                    display: table-row-group !important;
                  }
                  .no-print {
                    display: none !important;
                  }
                }
                
                table {
                  border-collapse: collapse;
                  width: 100%;
                }
                th, td {
                  border: 1px solid #ddd;
                  padding: 6px;
                }
                th {
                  background-color: #f4f4f4;
                }
              `}
            />
          </div>
        </div>

        <ScrollWrapper>
          <div ref={(el) => (componentRef = el)} className="max-w-3xl mx-auto p-6 bg-white text-gray-900 rounded-2xl shadow-md print:shadow-none print:max-w-full print:p-0">
            <header className="mb-6 text-center">
              <h3 className="text-lg font-semibold">{title}</h3>
              <p className="text-sm">
                Session ID: <span className="font-medium">{sessionId}</span>
              </p>
              <p className="text-sm text-gray-600 mt-2">Date: {dateRange}</p>
              
              {/* Summary Section */}
              <div className="mt-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-center mb-3">Summary</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-grey-50 rounded">
                    <div className="text-sm text-gray-600">Total Sales</div>
                    <div className="text-lg font-bold text-grey-600">
                      {formatCurrency(summary?.totalSales)}
                    </div>
                  </div>
                  <div className="p-3 bg-red-50 rounded">
                    <div className="text-sm text-gray-600">Total Returns</div>
                    <div className="text-lg font-bold text-red-600">
                      {formatCurrency(summary?.totalReturns)}
                    </div>
                  </div>
                  <div className="p-3 bg-grey-50 rounded">
                    <div className="text-sm text-gray-600">Net Sales</div>
                    <div className="text-lg font-bold text-grey-600">
                      {formatCurrency(summary?.netSales)}
                    </div>
                  </div>
                </div>
              </div>
            </header>

            <section className="mb-5">
              <h4 className="text-sm font-semibold mb-2">Sales Details</h4>
              <table className="w-full text-sm border-collapse">
                <thead className="sr-only">
                  <tr>
                    <th>Name</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s, i) => (
                    <tr 
                      key={i} 
                      className={`border-b last:border-b-0 ${
                        s.name === "Returns" ? "bg-red-50" : 
                        s.name === "Net Sales" ? "bg-grey-50 font-semibold" : ""
                      }`}
                    >
                      <td className="py-2">
                        <div className="font-medium">{s.name}</div>
                        {s.details && (
                          <div className="text-xs text-gray-600">{s.details}</div>
                        )}
                      </td>
                      <td className="py-2 text-right">{s.qty}</td>
                      <td className={`py-2 text-right font-medium ${
                        s.amount < 0 ? 'text-red-600' : 
                        s.name === "Net Sales" ? 'text-grey-600' : ''
                      }`}>
                        {formatCurrency(s.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="mb-5">
              <h4 className="text-sm font-semibold mb-2">Taxes on Sales</h4>
              <div className="border rounded overflow-hidden">
                <div className="flex justify-between p-3 border-b bg-gray-50">
                  <div className="font-medium">Sales Tax</div>
                  <div>{formatCurrency(taxes.breakdown?.salesTax || 0)}</div>
                </div>
                <div className="flex justify-between p-3 border-b">
                  <div className="font-medium">Returns Tax</div>
                  <div>{formatCurrency(taxes.breakdown?.returnsTax || 0)}</div>
                </div>
                <div className="flex justify-between p-3 bg-grey-50">
                  <div className="font-semibold">Net Tax</div>
                  <div className="font-semibold text-grey-600">
                    {formatCurrency(taxes.amount)}
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-5">
              <h4 className="text-sm font-semibold mb-2">Payments</h4>
              <ul className="text-sm border rounded overflow-hidden">
                {payments.map((p, i) => (
                  <li 
                    key={i} 
                    className={`flex justify-between py-2 px-3 ${
                      p.method === "Refunds" ? "bg-red-50" : 
                      "border-b last:border-b-0"
                    }`}
                  >
                    <span className={p.method === "Refunds" ? "font-semibold text-red-600" : ""}>
                      {p.method}
                      {p.note ? (
                        <span className="text-gray-600">/{p.note}</span>
                      ) : null}
                    </span>
                    <span className={`font-medium ${
                      p.amount < 0 ? 'text-red-600' : ''
                    }`}>
                      {formatCurrency(p.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mb-5">
              <h4 className="text-sm font-semibold mb-2">Discounts</h4>
              <div className="text-sm border rounded p-3">
                <div className="flex justify-between mb-2">
                  <span>Sales Discounts:</span>
                  <span className="font-medium">
                    {formatCurrency(discounts.breakdown?.salesDiscounts || 0)}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>Returns Discounts:</span>
                  <span className="font-medium">
                    {formatCurrency(discounts.breakdown?.returnsDiscounts || 0)}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>Number of discounts:</span>
                  <span className="font-medium">{discounts.count}</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-semibold">Net Discounts:</span>
                  <span className="font-semibold text-grey-600">
                    {formatCurrency(discounts.amount)}
                  </span>
                </div>
              </div>
            </section>

            <section className="mb-5">
              <h4 className="text-sm font-semibold mb-2">Session Control</h4>
              <div className="border rounded p-4 bg-gray-50">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Total Sales Transactions</div>
                    <div className="font-semibold">{sessionControl.salesTransactions}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Returns Transactions</div>
                    <div className="font-semibold">{sessionControl.returnsTransactions}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Transactions</div>
                    <div className="font-semibold">{sessionControl.transactions}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Net Sales Total</div>
                    <div className="font-semibold text-grey-600">
                      {formatCurrency(sessionControl.total)}
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between">
                    <div>
                      <div className="text-sm text-gray-600">Session Name</div>
                      <div className="font-semibold">{sessionControl.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Expected Amount</div>
                      <div className="font-semibold text-grey-600">
                        {formatCurrency(sessionControl.expected)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {note && (
              <footer className="text-sm text-gray-600 border-t pt-3">
                <div>Note: {note}</div>
              </footer>
            )}
          </div>
        </ScrollWrapper>
      </Loadercomponent>
    </Header>
  );
};

function formatCurrency(value) {
  const formattedValue = Number(value).toFixed(3);
  if (value < 0) {
    return `-${formattedValue}`;
  }
  return formattedValue;
}

function formatCurrencyPDF(value) {
  const formattedValue = Number(value).toFixed(3);
  if (value < 0) {
    return `-${formattedValue} OMR`;
  }
  return `${formattedValue} OMR`;
}

export default SalesReportReplica;